import React, { useCallback, useEffect, useState } from "react";

import {
  animated,
  useTransition,
  config as springConfig,
} from "@react-spring/web";
import ReplaceIcon from "@uui/Repeat03";
import { find, last } from "lodash/fp";

import type { SxProps } from "@mui/material";
import ClickAwayListener, {
  ClickAwayListenerProps,
} from "@mui/material/ClickAwayListener";

import SearchBox from "@app/analytics/components/SearchBox";
import useSearch from "@app/analytics/MeetingReport/hooks/useSearch";
import useTranscript from "@app/analytics/MeetingReport/hooks/useTranscript";
import useEditStore from "@app/analytics/MeetingReport/stores/edits";
import useSearchStore from "@app/analytics/MeetingReport/stores/search";
import useTranscriptStore from "@app/analytics/MeetingReport/stores/transcript";
import type {
  SingleNoteEdit,
  TranscriptEdit,
} from "@app/analytics/MeetingReport/types";
import {
  getNoteIdFromSearchResultId,
  isNoteSearchResult,
  isTranscriptSearchResult,
  SearchOptions,
} from "@app/analytics/MeetingReport/utils";
import {
  replaceAllInNoteItem,
  replaceAllInNotes,
  replaceAllInTranscript,
  replaceAllInTurn,
} from "@app/analytics/MeetingReport/utils/editUtils";
import useTimeout from "@app/common/hooks/useTimeout";

import Button from "@rui/Button";
import { Row } from "@rui/Stack";
import { roundedOutline } from "@rui/styles";

import {
  FindAndReplaceInput,
  FindAndReplaceWrapper,
  SearchBoxWrapper,
  Wrapper,
} from "./styles";

const AnimatedContainer = animated(Wrapper);

const SEARCH_BOX_STYLE_OVERRIDES = {
  height: "48px",
  px: 1,
  py: 0.5,
  border: "none",
  backgroundColor: "#fff",
  "&:focus-within": {
    backgroundColor: "#fff",
    ...roundedOutline({
      borderRadius: "0px",
      border: "none",
    }),
  },
};

const SEARCH_BOX_INPUT_STYLE_OVERRIDES = {
  fontSize: "0.875rem",
  backgroundColor: "#fff",
  border: "none",
};

interface MeetingReportSearchBoxProps {
  onClose: () => void;
  open: boolean;
  readOnly?: boolean;
  searchOptions?: SearchOptions;
  sx?: SxProps;
}

const MeetingReportSearchBox = ({
  readOnly = false,
  onClose,
  open,
  searchOptions,
  sx,
}: MeetingReportSearchBoxProps) => {
  const {
    searchMode,
    activeSearchResult,
    activeSearchResultIndex,
    goToNextSearchResult,
    goToPreviousSearchResult,
    searchResults,
    searchResultsByNoteTypeAndId,
    searchResultsByParagraphId,
    searchTerm,
  } = useSearchStore((state) => ({
    searchMode: state.searchMode,
    activeSearchResult: state.searchResults[state.activeSearchResultIndex],
    activeSearchResultIndex: state.activeSearchResultIndex,
    goToNextSearchResult: state.goToNextSearchResult,
    goToPreviousSearchResult: state.goToPreviousSearchResult,
    searchResultsByNoteTypeAndId: state.searchResultsByNoteTypeAndId,
    searchResultsByParagraphId: state.searchResultsByParagraphId,
    searchResults: state.searchResults,
    searchTerm: state.searchTerm,
  }));

  const {
    editedNotes,
    editingSection,
    getNoteItemsByType,
    saveEdit,
    transcriptTurns,
  } = useEditStore((state) => ({
    editedNotes: state.notes,
    editingSection: state.editingSection,
    saveEdit: state.save,
    transcriptTurns: state.transcript.turns,
    getNoteItemsByType: state.getItemsByType,
  }));

  const showFindAndReplace = !readOnly && searchMode === "replace";
  const setActiveItem = useTranscriptStore((state) => state.setActiveItem);

  const search = useSearch();

  const { wordDelimiter } = useTranscript();

  const [searchBoxValue, setSearchBoxValue] = useState(searchTerm);
  const replaceInputRef = React.useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (searchTerm !== searchBoxValue) {
      handleSearchBoxValueChange(searchTerm);
    }
  }, [searchTerm]);

  const handleClearSearch = () => {
    if (searchBoxValue) {
      setSearchBoxValue("");
    }
    search.clearSearch();
    onClose();
  };

  const openSearchBox = open || Boolean(searchBoxValue);

  const transition = useTransition(openSearchBox, {
    from: { opacity: 0, height: 0, minHeight: 0, paddingTop: 0 },
    enter: { opacity: 1, height: "auto", minHeight: 50, paddingTop: 8 },
    leave: { opacity: 0, height: 0, minHeight: 0, paddingTop: 0 },
    config: springConfig.stiff,
  });

  const performSearch = useCallback(() => {
    search.performSearch(searchBoxValue, searchOptions);
  }, [
    searchBoxValue,
    searchOptions,
    transcriptTurns,
    editedNotes.summary,
    editedNotes.chapters,
    editedNotes.topics,
    editedNotes.actionItems,
    editedNotes.keyQuestions,
    editedNotes.notes,
  ]);

  const handleSearchBoxValueChange = (newValue: string) => {
    setSearchBoxValue(newValue);
    if (!newValue) {
      search.clearSearch();
    }
  };

  const handleEnter = () => {
    if (searchTerm !== searchBoxValue) {
      performSearch();
    } else {
      goToNextSearchResult();
    }
  };

  const handleClickAway: ClickAwayListenerProps["onClickAway"] = (e) => {
    e.stopPropagation();
    if (searchTerm) return;
    onClose();
  };

  const handleReplaceAll = () => {
    const replacementValue = replaceInputRef.current?.value ?? "";

    if (editingSection === "transcript") {
      const edits = replaceAllInTranscript({
        replacementValue,
        searchResults: searchResultsByParagraphId,
        turns: transcriptTurns,
        wordDelimiter,
      });
      saveEdit({ edits });

      const lastEdit = last(edits);
      if (lastEdit) {
        setActiveItem({ id: lastEdit.after.id, type: "paragraph" }, false);
      }
      saveEdit({ edits });
    }

    if (editingSection === "notes") {
      const edits = replaceAllInNotes({
        replacementValue,
        searchResults: searchResultsByNoteTypeAndId,
        getNoteItemsByType,
      });
      saveEdit({ edits });
    }
  };

  const handleReplace = () => {
    if (!activeSearchResult) return;

    // replace current search result with value in replace input
    const replacementValue = replaceInputRef.current?.value ?? "";

    if (isTranscriptSearchResult(activeSearchResult)) {
      const turnWithSearchResult = find(
        (turn) => activeSearchResult.paragraphs.includes(turn.id),
        transcriptTurns
      );

      if (!turnWithSearchResult) return;

      const afterTurn = replaceAllInTurn(
        turnWithSearchResult,
        [activeSearchResult],
        replacementValue,
        wordDelimiter
      );

      const edit: TranscriptEdit = {
        type: "turn",
        before: turnWithSearchResult,
        after: afterTurn,
      };

      setActiveItem({ id: afterTurn.id, type: "paragraph" }, false);
      saveEdit(edit);
    }

    if (isNoteSearchResult(activeSearchResult)) {
      const noteItemToUpdate = find(
        { id: getNoteIdFromSearchResultId(activeSearchResult) },
        getNoteItemsByType(activeSearchResult.note.type)
      );

      if (!noteItemToUpdate) return;

      const after = replaceAllInNoteItem(
        noteItemToUpdate,
        [activeSearchResult],
        replacementValue
      );

      const edit = {
        type: activeSearchResult.note.type,
        before: noteItemToUpdate,
        after,
      } as SingleNoteEdit;

      saveEdit(edit);
    }
  };

  useTimeout(performSearch, 200, !readOnly && open, [performSearch]);

  return transition(
    (style, shouldShow) =>
      shouldShow && (
        <ClickAwayListener onClickAway={handleClickAway}>
          <AnimatedContainer style={style} sx={sx}>
            <SearchBoxWrapper>
              <SearchBox
                autoFocus={open}
                onChange={handleSearchBoxValueChange}
                onClear={handleClearSearch}
                onEnter={handleEnter}
                placeholder="Search"
                value={searchBoxValue}
                readOnly={readOnly}
                showPagination={Boolean(searchTerm)}
                sx={SEARCH_BOX_STYLE_OVERRIDES}
                inputSx={SEARCH_BOX_INPUT_STYLE_OVERRIDES}
                pagination={{
                  currentPage: Math.min(
                    activeSearchResultIndex + 1,
                    searchResults.length
                  ),
                  goToNextPage: goToNextSearchResult,
                  goToPreviousPage: goToPreviousSearchResult,
                  numPages: searchResults.length,
                }}
              />
              {showFindAndReplace && (
                <FindAndReplaceWrapper variant="apart">
                  <FindAndReplaceInput
                    inputRef={replaceInputRef}
                    disableUnderline
                    placeholder="Replace with..."
                    startAdornment={<ReplaceIcon />}
                  />
                  <Row variant="right" spacing={1}>
                    <Button
                      variant="outlined"
                      sx={{ height: "36px" }}
                      onClick={handleReplace}
                      disabled={!activeSearchResult}
                    >
                      Replace
                    </Button>
                    <Button
                      variant="outlined"
                      sx={{ height: "36px" }}
                      onClick={handleReplaceAll}
                      disabled={!activeSearchResult}
                    >
                      Replace All
                    </Button>
                  </Row>
                </FindAndReplaceWrapper>
              )}
            </SearchBoxWrapper>
          </AnimatedContainer>
        </ClickAwayListener>
      )
  );
};

export default MeetingReportSearchBox;
